import type AutomationID from '@/App/Automation/Entity/AutomationID'
import type AvailabilityID from '@/App/Availability/Entity/AvailabilityID'
import type BookingID from '@/App/Booking/Booking/Entity/BookingID'
import type DraftMeetingID from '@/App/Booking/Draft/Entity/DraftMeetingID'
import type CompanyCustomerID from '@/App/Company/Entity/CompanyCustomerID'
import CompanyID from '@/App/Company/Entity/CompanyID'
import CompanyUserID from '@/App/CompanyUser/Entity/CompanyUserID'
import CompanyUserInviteID from '@/App/CompanyUserInvite/Entity/CompanyUserInviteID'
import IntegrationID from '@/App/Integration/Entity/IntegrationID'
import PlanID from '@/App/Plan/Entity/PlanID'
import type ServiceID from '@/App/Service/Entity/ServiceID'
import type { LocaleEnum } from '@/Shared/AutoGenerated/LocalizationEnum'
import {
    CMSArticleSlugEnum,
    CMSCategorySlugEnum,
    CMSSubcategorySlugEnum,
    MeetingSearchFiltersDateTypeEnum,
    PlanFeaturesAccessEnum,
    PlanFeaturesQuantityEnum,
    UserSocialAuthenticationActionEnum,
    type GeneratedBusinessName,
} from '@/Shared/AutoGenerated/Main'
import {
    RequestQueryKeyEnum,
    RouteParamKeyEnum,
} from '@/Shared/AutoGenerated/RequestFieldEnum'
import {
    RoutePathAppEnum,
    RoutePathBlogEnum,
    RoutePathBookingFormEnum,
    RoutePathCookieConsentEnum,
    RoutePathCustomerEnum,
    RoutePathHelpCenterEnum,
    RoutePathMarketingEnum,
    RoutePathMeetingEnum,
    RoutePathFreeToolEnum,
} from '@/Shared/AutoGenerated/RoutePathEnum'
import type { UrlFactoryInterface } from '@/Shared/AutoGenerated/UrlFactoryInterface'
import useUrlGenerator, {
    type UrlQueryParam,
} from '@/Shared/Hook/useUrlGenerator'

export abstract class AbstractUrlFactory implements UrlFactoryInterface {
    // -----------------------------------
    // DOMAIN: Admin/Queue
    // -----------------------------------

    public makeAdminQueueProcessedPageUrl(
        jobState?: string,
        queueName?: string,
        page?: string,
    ): string {
        const queryParams: UrlQueryParam[] = []

        if (jobState !== undefined) {
            queryParams.push({
                key: RequestQueryKeyEnum.Admin_Queue_JobState,
                value: jobState,
            })
        }

        if (queueName !== undefined) {
            queryParams.push({
                key: RequestQueryKeyEnum.Admin_Queue_QueueName,
                value: queueName,
            })
        }

        if (page !== undefined) {
            queryParams.push({
                key: RequestQueryKeyEnum.PaginationPage,
                value: page,
            })
        }

        return useUrlGenerator().app(
            RoutePathAppEnum.Admin_Queue_Processed,
            queryParams,
        )
    }

    public makeAdminQueuePendingStatsUrl(
        jobState?: string,
        queueName?: string,
    ): string {
        return useUrlGenerator().app(
            RoutePathAppEnum.Admin_Queue_Pending_Stats,
            this.makeAdminQueueFilterQueryParams(jobState, queueName),
        )
    }

    public makeAdminQueuePendingPageUrl(
        jobState?: string,
        queueName?: string,
    ): string {
        return useUrlGenerator().app(
            RoutePathAppEnum.Admin_Queue_Pending,
            this.makeAdminQueueFilterQueryParams(jobState, queueName),
        )
    }

    public makeAdminQueueRetryUrl(
        jobId: number,
        jobState?: string,
        queueName?: string,
        page?: number,
    ): string {
        const queryParams = this.makeAdminQueueFilterQueryParams(
            jobState,
            queueName,
        )

        queryParams.unshift({
            key: RequestQueryKeyEnum.Admin_Queue_JobId,
            value: jobId,
        })

        if (page !== undefined) {
            queryParams.push({
                key: RequestQueryKeyEnum.PaginationPage,
                value: page,
            })
        }

        return useUrlGenerator().app(
            RoutePathAppEnum.Admin_Queue_Retry,
            queryParams,
        )
    }

    private makeAdminQueueFilterQueryParams(
        jobState?: string,
        queueName?: string,
    ): UrlQueryParam[] {
        const queryParams: UrlQueryParam[] = []

        if (jobState !== undefined) {
            queryParams.push({
                key: RequestQueryKeyEnum.Admin_Queue_JobState,
                value: jobState,
            })
        }

        if (queueName !== undefined) {
            queryParams.push({
                key: RequestQueryKeyEnum.Admin_Queue_QueueName,
                value: queueName,
            })
        }

        return queryParams
    }

    // -----------------------------------
    // DOMAIN: Admin/User
    // -----------------------------------

    public makeAdminUsersImpersonationStoreUrl(
        companyUserId: CompanyUserID,
    ): string {
        return useUrlGenerator().app(
            RoutePathAppEnum.Admin_Users_EnterImpersonateMode,
            [
                {
                    key: RequestQueryKeyEnum.CompanyUserId,
                    value: companyUserId.value,
                },
            ],
        )
    }

    public makeAdminUsersPageUrl(search?: string): string {
        const queryParams: UrlQueryParam[] = []

        if (search !== undefined) {
            queryParams.push({
                key: RequestQueryKeyEnum.Admin_Users_Search,
                value: search,
            })
        }

        return useUrlGenerator().app(RoutePathAppEnum.Admin_Users, queryParams)
    }

    // -----------------------------------
    // DOMAIN: App/Automation
    // -----------------------------------

    public makeAutomationEditPageUrl(automationId: AutomationID): string {
        return useUrlGenerator().app(RoutePathAppEnum.Automations_Edit, [
            {
                key: RequestQueryKeyEnum.AutomationId,
                value: automationId.value,
            },
        ])
    }

    // -----------------------------------
    // DOMAIN: App/Availability
    // -----------------------------------

    public makeAvailabilityEditPageUrl(availabilityId: AvailabilityID): string {
        return useUrlGenerator().app(RoutePathAppEnum.Availabilities_Edit, [
            {
                key: RequestQueryKeyEnum.AvailabilityId,
                value: availabilityId.value,
            },
        ])
    }

    // -----------------------------------
    // DOMAIN: App/Booking
    // -----------------------------------

    public makeMeetingIndexPageUrl(meetingUniqueCode: string): string {
        return useUrlGenerator().app(RoutePathAppEnum.Meetings, [
            {
                key: RequestQueryKeyEnum.App_IndexMeetings_Filter_Search,
                value: meetingUniqueCode,
            },
        ])
    }

    public makeMeetingIndexAllDatesPageUrl(meetingUniqueCode: string): string {
        return useUrlGenerator().app(RoutePathAppEnum.Meetings, [
            {
                key: RequestQueryKeyEnum.App_IndexMeetings_Filter_Search,
                value: meetingUniqueCode,
            },
            {
                key: RequestQueryKeyEnum.App_IndexMeetings_Filter_DateType,
                value: MeetingSearchFiltersDateTypeEnum.All,
            },
        ])
    }

    public makeRecurringMeetingIndexPageUrl(bookingUniqueCode: string): string {
        return useUrlGenerator().app(RoutePathAppEnum.Recurring, [
            {
                key: RequestQueryKeyEnum.App_IndexRecurringMeetings_Filter_Search,
                value: bookingUniqueCode,
            },
        ])
    }

    // -----------------------------------
    // DOMAIN: App/Company
    // -----------------------------------

    public makeAppHomePageUrl(): string {
        return useUrlGenerator().app(RoutePathAppEnum.Root)
    }

    // -----------------------------------
    // DOMAIN: App/CompanyUser
    // -----------------------------------

    public makeCompanyUserDeleteUrl(companyUserId: CompanyUserID): string {
        return useUrlGenerator().app(RoutePathAppEnum.CompanyUsers_Delete, [
            {
                key: RequestQueryKeyEnum.CompanyUserId,
                value: companyUserId.value,
            },
        ])
    }

    public makeCompanyUserEditPageUrl(companyUserId: CompanyUserID): string {
        return useUrlGenerator().app(RoutePathAppEnum.CompanyUsers_Edit, [
            {
                key: RequestQueryKeyEnum.CompanyUserId,
                value: companyUserId.value,
            },
        ])
    }

    public makeCompanyUserUpdateUrl(companyUserId: CompanyUserID): string {
        return useUrlGenerator().app(RoutePathAppEnum.CompanyUsers_Update, [
            {
                key: RequestQueryKeyEnum.CompanyUserId,
                value: companyUserId.value,
            },
        ])
    }

    // -----------------------------------
    // DOMAIN: App/CompanyUserInvite
    // -----------------------------------

    public makeAcceptInvitePageUrl(inviteId: CompanyUserInviteID): string {
        return useUrlGenerator().app(
            RoutePathAppEnum.CompanyUsers_Invites_Accept,
            [{ key: RequestQueryKeyEnum.InviteId, value: inviteId.value }],
        )
    }

    public makeCompanyUserInviteDeleteUrl(
        inviteId: CompanyUserInviteID,
    ): string {
        return useUrlGenerator().app(
            RoutePathAppEnum.CompanyUsers_Invites_Delete,
            [{ key: RequestQueryKeyEnum.InviteId, value: inviteId.value }],
        )
    }

    // -----------------------------------
    // DOMAIN: App/Integration
    // -----------------------------------

    public makeIntegrationDeleteUrl(integrationId: IntegrationID): string {
        return useUrlGenerator().app(RoutePathAppEnum.Integrations_Delete, [
            {
                key: RequestQueryKeyEnum.IntegrationId,
                value: integrationId.value,
            },
        ])
    }

    public makeIntegrationRedirectUrl(redirectRoute: RoutePathAppEnum): string {
        return useUrlGenerator().app(redirectRoute)
    }

    public makeIntegrationsPageUrl(): string {
        return useUrlGenerator().app(RoutePathAppEnum.Integrations)
    }

    public makeOutlookCalendarOAuthCallbackUrl(): string {
        return useUrlGenerator().app(
            RoutePathAppEnum.Integrations_OutlookCalendar_Callback,
        )
    }

    public makeZoomOAuthCallbackUrl(): string {
        return useUrlGenerator().app(
            RoutePathAppEnum.Integrations_Zoom_Callback,
        )
    }

    public makeGoogleMeetOAuthCallbackUrl(): string {
        return useUrlGenerator().app(
            RoutePathAppEnum.Integrations_GoogleMeet_Callback,
        )
    }

    public makeGoogleCalendarOAuthCallbackUrl(): string {
        return useUrlGenerator().app(
            RoutePathAppEnum.Integrations_GoogleCalendar_Callback,
        )
    }

    public makeStripeConnectOnboardRefreshRedirectUrl(): string {
        return useUrlGenerator().app(
            RoutePathAppEnum.Integrations_StripeConnect_Redirect,
        )
    }

    // -----------------------------------
    // DOMAIN: App/Service
    // -----------------------------------

    public makeServiceEditPageUrl(serviceId: ServiceID): string {
        return useUrlGenerator().app(RoutePathAppEnum.Services_Edit, [
            { key: RequestQueryKeyEnum.ServiceId, value: serviceId.value },
        ])
    }

    // -----------------------------------
    // DOMAIN: App/Subscription
    // -----------------------------------

    public makeBillingChangePlanPageUrl(): string {
        return useUrlGenerator().app(RoutePathAppEnum.Billing_Plans_Change)
    }

    public makeBillingPaddleCheckoutPageUrl(planId: PlanID): string {
        return useUrlGenerator().app(RoutePathAppEnum.Billing_Paddle_Checkout, [
            { key: RequestQueryKeyEnum.PlanId, value: planId.value },
        ])
    }

    public makeBillingPaddleCustomerPortalRedirectUrl(): string {
        return useUrlGenerator().app(
            RoutePathAppEnum.Billing_Paddle_CustomerPortal,
        )
    }

    public makeBillingPaddlePlanUpdateUrl(planId: PlanID): string {
        return useUrlGenerator().app(
            RoutePathAppEnum.Billing_Paddle_ChangePlan,
            [{ key: RequestQueryKeyEnum.PlanId, value: planId.value }],
        )
    }

    public makeBillingSubscriptionPlansChangePageUrl(
        featureType: PlanFeaturesAccessEnum | PlanFeaturesQuantityEnum,
        featureQuantity: number,
    ): string {
        return useUrlGenerator().app(RoutePathAppEnum.Billing_Plans_Change, [
            {
                key: RequestQueryKeyEnum.App_Billing_FeatureType,
                value: featureType,
            },
            {
                key: RequestQueryKeyEnum.App_Billing_FeatureQuantity,
                value: featureQuantity,
            },
        ])
    }

    public makeBillingSubscriptionPlansIndexPageUrl(): string {
        return useUrlGenerator().app(RoutePathAppEnum.Billing_Plans_Index)
    }

    // -----------------------------------
    // DOMAIN: App/User
    // -----------------------------------

    public makeAuthCompanySelectionStoreUrl(
        companyId: CompanyID,
        destinationUrl?: string,
    ): string {
        const queryParams: UrlQueryParam[] = [
            { key: RequestQueryKeyEnum.CompanyId, value: companyId.value },
        ]

        if (destinationUrl !== undefined) {
            queryParams.push({
                key: RequestQueryKeyEnum.DestinationUrl,
                value: destinationUrl,
            })
        }

        return useUrlGenerator().app(
            RoutePathAppEnum.Auth_Login_Companies,
            queryParams,
        )
    }

    public makeAuthForgotPasswordPageUrl(): string {
        return useUrlGenerator().app(RoutePathAppEnum.Auth_ForgotMyPassword)
    }

    public makeAuthLoginPageUrl(): string {
        return useUrlGenerator().app(RoutePathAppEnum.Auth_Login)
    }

    public makeAuthRegisterPageUrl(): string {
        return useUrlGenerator().app(RoutePathAppEnum.Auth_Register)
    }

    public makeEmailVerificationActionUrl(token: string): string {
        return useUrlGenerator().app(RoutePathAppEnum.Auth_VerifyEmail, [
            {
                key: RequestQueryKeyEnum.Auth_User_EmailVerificationToken,
                value: token,
            },
        ])
    }

    public makeGoogleOAuthLoginCallbackUrl(): string {
        return useUrlGenerator().app(
            RoutePathAppEnum.Auth_User_Social_Google_Callback_Login,
        )
    }

    public makeGoogleOAuthRegisterCallbackUrl(): string {
        return useUrlGenerator().app(
            RoutePathAppEnum.Auth_User_Social_Google_Callback_Register,
        )
    }

    public makeGoogleOAuthBusinessNameGeneratorRegisterCallbackUrl(): string {
        throw new Error('Dead')
    }

    public makeMicrosoftOAuthBusinessNameGeneratorRegisterCallbackUrl(): string {
        throw new Error('Dead')
    }

    public makePasswordResetPageUrl(token: string): string {
        return useUrlGenerator().app(RoutePathAppEnum.Auth_CreateNewPassword, [
            {
                key: RequestQueryKeyEnum.Auth_User_PasswordResetToken,
                value: token,
            },
        ])
    }

    public makeRelativeAuthCompanySelectionPageUrl(
        destinationUrl?: string,
    ): string {
        const queryParams: UrlQueryParam[] = []

        if (destinationUrl !== undefined) {
            queryParams.push({
                key: RequestQueryKeyEnum.DestinationUrl,
                value: destinationUrl,
            })
        }

        return useUrlGenerator().app(
            RoutePathAppEnum.Auth_Login_Companies,
            queryParams,
        )
    }

    public makeRelativeAuthLoginPageUrl(destinationUrl: string): string {
        return useUrlGenerator().app(RoutePathAppEnum.Auth_Login, [
            { key: RequestQueryKeyEnum.DestinationUrl, value: destinationUrl },
        ])
    }

    public makeMicrosoftOAuthCallbackUrl(
        authenticationAction: UserSocialAuthenticationActionEnum,
    ): string {
        const route =
            authenticationAction === UserSocialAuthenticationActionEnum.Register
                ? RoutePathAppEnum.Auth_User_Social_Microsoft_Callback_Register
                : RoutePathAppEnum.Auth_User_Social_Microsoft_Callback_Login

        return useUrlGenerator().app(route)
    }

    // -----------------------------------
    // DOMAIN: BookingForm/BookingForm
    // -----------------------------------

    public makeBookingFormRootRedirectUrl(): string {
        return useUrlGenerator().bookingForm(
            RoutePathBookingFormEnum.PrefixedRoot,
        )
    }

    public makeCompanyBookingFormPageUrl(companySlug: string): string {
        throw new Error('Dead')
    }

    public makeServiceBookingFormPageUrl(
        companySlug: string,
        serviceSlug: string,
    ): string {
        return useUrlGenerator().bookingForm(
            RoutePathBookingFormEnum.Step_CompanyUser,
            [],
            [
                { key: RouteParamKeyEnum.CompanySlug, value: companySlug },
                { key: RouteParamKeyEnum.ServiceSlug, value: serviceSlug },
            ],
        )
    }

    public makeBookingFormCompanyServicesPageUrl(
        companySlug: string,
        locale: LocaleEnum,
    ): string {
        return useUrlGenerator().bookingForm(
            RoutePathBookingFormEnum.Step_Service,
            [{ key: RequestQueryKeyEnum.Locale, value: locale }],
            [{ key: RouteParamKeyEnum.CompanySlug, value: companySlug }],
        )
    }

    public makeBookingFormCompanyUserPageUrl(
        companySlug: string,
        serviceSlug: string,
        locale: LocaleEnum,
    ): string {
        return useUrlGenerator().bookingForm(
            RoutePathBookingFormEnum.Step_CompanyUser,
            [{ key: RequestQueryKeyEnum.Locale, value: locale }],
            [
                { key: RouteParamKeyEnum.CompanySlug, value: companySlug },
                { key: RouteParamKeyEnum.ServiceSlug, value: serviceSlug },
            ],
        )
    }

    public makeBookingFormCompanyUserWithDraftPageUrl(
        companySlug: string,
        serviceSlug: string,
        draftMeetingId: DraftMeetingID,
        locale: LocaleEnum,
    ): string {
        return useUrlGenerator().bookingForm(
            RoutePathBookingFormEnum.Step_CompanyUser,
            [
                {
                    key: RequestQueryKeyEnum.DraftMeetingId,
                    value: draftMeetingId.value,
                },
                { key: RequestQueryKeyEnum.Locale, value: locale },
            ],
            [
                { key: RouteParamKeyEnum.CompanySlug, value: companySlug },
                { key: RouteParamKeyEnum.ServiceSlug, value: serviceSlug },
            ],
        )
    }

    public makeBookingFormDatePageUrl(
        companySlug: string,
        serviceSlug: string,
        draftMeetingId: DraftMeetingID,
        locale: LocaleEnum,
    ): string {
        return useUrlGenerator().bookingForm(
            RoutePathBookingFormEnum.Step_Date,
            [
                {
                    key: RequestQueryKeyEnum.DraftMeetingId,
                    value: draftMeetingId.value,
                },
                { key: RequestQueryKeyEnum.Locale, value: locale },
            ],
            [
                { key: RouteParamKeyEnum.CompanySlug, value: companySlug },
                { key: RouteParamKeyEnum.ServiceSlug, value: serviceSlug },
            ],
        )
    }

    public makeBookingFormConfirmationPageUrl(
        companySlug: string,
        serviceSlug: string,
        draftMeetingId: DraftMeetingID,
        locale: LocaleEnum,
    ): string {
        return useUrlGenerator().bookingForm(
            RoutePathBookingFormEnum.Step_Confirmation,
            [
                {
                    key: RequestQueryKeyEnum.DraftMeetingId,
                    value: draftMeetingId.value,
                },
                { key: RequestQueryKeyEnum.Locale, value: locale },
            ],
            [
                { key: RouteParamKeyEnum.CompanySlug, value: companySlug },
                { key: RouteParamKeyEnum.ServiceSlug, value: serviceSlug },
            ],
        )
    }

    public makeBookingFormSuccessPageUrl(
        companySlug: string,
        serviceSlug: string,
        bookingId: BookingID,
        companyCustomerId: CompanyCustomerID,
        locale: LocaleEnum,
    ): string {
        return useUrlGenerator().bookingForm(
            RoutePathBookingFormEnum.Step_Success,
            [
                { key: RequestQueryKeyEnum.BookingId, value: bookingId.value },
                {
                    key: RequestQueryKeyEnum.CompanyCustomerId,
                    value: companyCustomerId.value,
                },
                { key: RequestQueryKeyEnum.Locale, value: locale },
            ],
            [
                { key: RouteParamKeyEnum.CompanySlug, value: companySlug },
                { key: RouteParamKeyEnum.ServiceSlug, value: serviceSlug },
            ],
        )
    }

    // -----------------------------------
    // DOMAIN: CookieConsent/CookieConsent
    // -----------------------------------

    public makeCookieConsentUpsertUrl(): string {
        return useUrlGenerator().cookieConsent(
            RoutePathCookieConsentEnum.Upsert,
        )
    }

    // -----------------------------------
    // DOMAIN: Customer/Auth
    // -----------------------------------

    public makeCustomerCreateNewPasswordPageUrl(token: string): string {
        return useUrlGenerator().customer(
            RoutePathCustomerEnum.Auth_CreateNewPassword,
            [
                {
                    key: RequestQueryKeyEnum.Customer_NewPassword_Token,
                    value: token,
                },
            ],
        )
    }

    public makeCustomerGoogleOAuthCallbackUrl(): string {
        return useUrlGenerator().customer(
            RoutePathCustomerEnum.Auth_Customer_Social_Google_Callback,
        )
    }

    // -----------------------------------
    // DOMAIN: Docs/HelpCenter
    // -----------------------------------

    public makeHelpCenterArticlePageUrl(
        articleSlug: CMSArticleSlugEnum,
    ): string {
        return useUrlGenerator().helpCenter(
            RoutePathHelpCenterEnum.Articles_Show,
            [],
            [{ key: RouteParamKeyEnum.ArticleSlug, value: articleSlug }],
        )
    }

    public makeHelpCenterCategoryPageUrl(
        categorySlug: CMSCategorySlugEnum,
    ): string {
        return useUrlGenerator().helpCenter(
            RoutePathHelpCenterEnum.Articles_Index,
            [],
            [{ key: RouteParamKeyEnum.CategorySlug, value: categorySlug }],
        )
    }

    public makeHelpCenterHomePageUrl(): string {
        return useUrlGenerator().helpCenter(RoutePathHelpCenterEnum.Home_Show)
    }

    public makeHelpCenterSubcategoryPageUrl(
        categorySlug: CMSCategorySlugEnum,
        subcategorySlug: CMSSubcategorySlugEnum,
    ): string {
        return `${this.makeHelpCenterCategoryPageUrl(categorySlug)}#${subcategorySlug}`
    }

    // -----------------------------------
    // DOMAIN: FreeTool/BusinessNameGenerator
    // -----------------------------------

    public makeFreeToolRoutePageUrl(
        freeToolRoute: RoutePathFreeToolEnum,
    ): string {
        return useUrlGenerator().freeTool(freeToolRoute)
    }

    public makeBusinessNameGeneratorStepResultPreviewUrl(
        generatedBusinessName: GeneratedBusinessName,
    ): string {
        return useUrlGenerator().freeTool(
            RoutePathFreeToolEnum.BusinessNameGenerator_StepResultPreview,
            [],
            [
                {
                    key: RouteParamKeyEnum.BusinessNameSuggestionId,
                    value: generatedBusinessName.randomId,
                },
            ],
        )
    }

    // -----------------------------------
    // DOMAIN: Marketing/Blog
    // -----------------------------------

    public makeBlogArticlePageUrl(articleSlug: CMSArticleSlugEnum): string {
        return useUrlGenerator().blog(
            RoutePathBlogEnum.Article_Show,
            [],
            [{ key: RouteParamKeyEnum.ArticleSlug, value: articleSlug }],
        )
    }

    public makeBlogCategoryPageUrl(categorySlug: CMSCategorySlugEnum): string {
        return useUrlGenerator().blog(
            RoutePathBlogEnum.Category_Show,
            [],
            [{ key: RouteParamKeyEnum.CategorySlug, value: categorySlug }],
        )
    }

    public makeBlogHomePageUrl(): string {
        return useUrlGenerator().blog(RoutePathBlogEnum.Home_Show)
    }

    // -----------------------------------
    // DOMAIN: Marketing/LandingPage
    // -----------------------------------

    public makeLandingHomePageUrl(): string {
        return useUrlGenerator().landingPage(RoutePathMarketingEnum.Empty)
    }

    public makeMarketingPrivacyPolicyPageUrl(): string {
        return useUrlGenerator().landingPage(
            RoutePathMarketingEnum.PrivacyPolicy,
        )
    }

    public makeMarketingRoutePageUrl(
        marketingRoute: RoutePathMarketingEnum,
    ): string {
        return useUrlGenerator().landingPage(marketingRoute)
    }

    public makeMarketingTermsOfServicePageUrl(): string {
        return useUrlGenerator().landingPage(
            RoutePathMarketingEnum.TermsOfService,
        )
    }

    // -----------------------------------
    // DOMAIN: Meeting/Meeting
    // -----------------------------------

    public makeGuestMeetingViewPageUrl(
        bookingId: BookingID,
        companyCustomerId: CompanyCustomerID,
    ): string {
        return useUrlGenerator().meeting(RoutePathMeetingEnum.View, [
            { key: RequestQueryKeyEnum.BookingId, value: bookingId.value },
            {
                key: RequestQueryKeyEnum.CompanyCustomerId,
                value: companyCustomerId.value,
            },
        ])
    }
}
